import { PublicPolicyPage, YouTubePolicySettingsPage } from './pages/ApplicationPolicy';
import { PublishingStudioLayout, PublishingWorkspacePage, YouTubeComposerPage, PublishingReviewPage, PublishingDeliveryPage, YouTubeLibraryPage, YouTubeVideoDetailPage, YouTubeAnalyticsPage, YouTubeReadinessPage } from "./pages/publishing/PublishingStudio";
import { CampaignActionPlanPage } from "./pages/CampaignActionPlanPage";
import { CampaignActionPlanRunPage } from "./pages/CampaignActionPlanRunPage";
import { CampaignActionPlanSchedulePage } from "./pages/CampaignActionPlanSchedulePage";
import { AnalyticsSourceSettingsPage } from "./pages/AnalyticsSourceSettingsPage";
import { NotFound } from "./pages/NotFound";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthGuard } from "./components/layout/AuthGuard";
import { Layout } from "./components/layout/Layout";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";
import { Landing } from "./pages/Landing";
import { Onboarding } from "./pages/Onboarding";
import { Brand } from "./pages/Brand";
import { WorkspaceShell } from "./components/workspace/WorkspaceShell";
import { Corpus } from "./pages/Corpus";
import { Ideas } from "./pages/Ideas";
import { Chat } from "./pages/Chat";
import { BrandReview } from "./pages/BrandReview";
import { Review } from "./pages/Review";
import { Performance } from "./pages/Performance";
import { Usage } from "./pages/Usage";
import { Playbooks } from "./pages/Playbooks";
import { Skills } from "./pages/Skills";
import { AgentUIPage } from "./pages-v2/AgentUIPage";
import { BrandDocsPage } from "./pages-v2/BrandDocsPage";
import { CampaignEditor } from "./pages/CampaignEditor";
import { Campaigns } from "./pages/Campaigns";
import { CampaignDetail } from "./pages/CampaignDetail";
import { ContentPreviewPage } from "./pages/ContentPreviewPage";
import { CampaignDailySetupPage } from "./pages/CampaignDailySetupPage";
import { SavedActionsPage } from "./pages/SavedActionsPage";
import { NewConfiguredActionPage } from "./pages/NewConfiguredActionPage";
import { ConfiguredActionPage } from "./pages/ConfiguredActionPage";
import { CampaignSourcePickerPage, CampaignActionPickerPage } from "./pages/CampaignSourcePickerPage";
import { Settings } from "./pages/Settings";
import { ApiTokensPage } from "./pages/ApiTokensPage";
import { ConnectionsSettingsPage } from "./pages/ConnectionsSettingsPage";
import { ConnectionDetailPage } from "./components/account/ConnectionDetailPage";
import { Runs } from "./pages/Runs";
import { Inbox } from "./pages/Inbox";
import { RunDetail } from "./pages/RunDetail";
import { RunTracePage } from "./pages/RunTracePage";
import { Outputs } from "./pages/Outputs";
import { OutputDetail } from "./pages/OutputDetail";
import { BrandResearch } from "./pages/BrandResearch";
import { ResearchRunDetail } from "./pages/ResearchRunDetail";
import { BrandResearchPackage } from "./pages/BrandResearchPackage";
import { TasksPage } from "./pages-v2/TasksPage";
import { StartJobPage } from "./pages/StartJobPage";
import { GateReview } from "./pages/GateReview";
import { Diagnostics } from "./pages/Diagnostics";
import { WorkflowsPage } from "./pages-v2/WorkflowsPage";
import { AnalyticsIndexPage, AnalyticsKeywordPage, AnalyticsPropertyPage, AnalyticsReferrerSourcePage, AnalyticsSourcePage } from "./pages-v2/AnalyticsPropertyPages";
import { DataViewVariantReviewPage, DataViewsPage } from "./pages/DataViewsPage";
import {
  BrandLayout,
  BrandConnectionsPage,
  BrandCorrectionsPage,
  BrandProfilePage,
  BrandSurfacesPage,
  BrandVisualsPage,
} from "./pages/BrandLayout";
import { BrandSettingsLayout } from "./pages/BrandSettingsLayout";
import { BrandLegacyRedirect } from "./components/routing/BrandLegacyRedirect";
import { VoiceDiscovery } from "./pages/VoiceDiscovery";
import { AssetLibrary } from "./pages/AssetLibrary";
import { ShopifyImportDetailPage, ShopifyImportListPage } from "./pages/ShopifyImportJob";
import { useActiveBrand } from "./hooks/useActiveBrand";
import { buildBrandReviewPath, parseBrandIdFromSearch } from "./lib/brand-navigation";
import { BrandArtifactWorkspace } from "./pages/BrandArtifactWorkspace";
import { WorkspacePage } from "./pages-v2/WorkspacePage";
import { BrandVoiceSettings } from "./pages/BrandVoiceSettings";
import { BrandContextDocs } from "./pages/BrandContextDocs";
import { Brands } from "./pages/Brands";
import { BrandCreate } from "./pages/BrandCreate";
import { PropertyContentCatalogPage, PropertyContentDetailPage } from "./pages/PropertyContentCatalog";
import { CampaignDailyOutputRevision } from "./pages/CampaignDailyOutputRevision";

function CampaignsRouteEntry() {
  const location = useLocation();
  const brandIdFromSearch = parseBrandIdFromSearch(location.search);
  const { activeBrandId, isLoading } = useActiveBrand();
  const targetBrandId = brandIdFromSearch ?? activeBrandId ?? null;

  if (!brandIdFromSearch && isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!targetBrandId) {
    return <Navigate to="/home" replace />;
  }

  return <Navigate to={`/brand/${encodeURIComponent(targetBrandId)}/campaigns`} replace />;
}

function ReviewRouteEntry() {
  const location = useLocation();
  const brandIdFromSearch = parseBrandIdFromSearch(location.search);
  const { activeBrandId, isLoading } = useActiveBrand();
  const targetBrandId = brandIdFromSearch ?? activeBrandId ?? null;

  if (!brandIdFromSearch && isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!targetBrandId) {
    return <BrandReview />;
  }

  return <Navigate to={buildBrandReviewPath(targetBrandId, location.search)} replace />;
}

function BillingRedirect() {
  const location = useLocation();
  return <Navigate to={`/usage${location.search}${location.hash}`} replace />;
}

export function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/privacy" element={<PublicPolicyPage kind="privacy" />} />
      <Route path="/terms" element={<PublicPolicyPage kind="terms" />} />
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />

      {/* Full-page content preview (no sidebar): the post as its audience will see it. */}
      <Route
        path="/brand/:brandId/campaigns/:campaignId/briefs/:briefId/preview"
        element={<AuthGuard><ContentPreviewPage /></AuthGuard>}
      />

      {/* Protected routes with sidebar */}
      <Route
        element={
          <AuthGuard>
            <Layout />
          </AuthGuard>
        }
      >
        <Route path="/brand/:brandId/campaigns" element={<Campaigns />} />
        <Route path="/brand/:brandId/campaigns/new" element={<CampaignEditor />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/edit" element={<CampaignEditor />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/daily-setup" element={<CampaignDailySetupPage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/sources" element={<CampaignSourcePickerPage />} />
        <Route path="/brand/:brandId/actions" element={<SavedActionsPage />} />
        <Route path="/brand/:brandId/actions/new" element={<NewConfiguredActionPage />} />
        <Route path="/brand/:brandId/actions/:origin/:actionId" element={<ConfiguredActionPage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/actions" element={<CampaignActionPickerPage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/action-plans/new" element={<CampaignActionPlanPage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/action-plans/:planId/edit" element={<CampaignActionPlanPage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/action-plans/:planId/restore" element={<CampaignActionPlanPage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/action-plans/:planId/run" element={<CampaignActionPlanRunPage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/action-plans/:planId/schedule" element={<CampaignActionPlanSchedulePage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/action-plans/:planId/schedule/edit" element={<CampaignActionPlanSchedulePage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/action-plans/:planId" element={<CampaignActionPlanPage />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/work/new" element={<CampaignDetail />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/outputs/:outputId/revisions/:revisionId" element={<CampaignDailyOutputRevision />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/packets/new" element={<CampaignDetail />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/briefs/:briefId/schedule" element={<CampaignDetail />} />
        <Route path="/brand/:brandId/campaigns/:campaignId" element={<CampaignDetail />} />
        <Route path="/brand/:brandId/campaigns/:campaignId/briefs/:briefId" element={<CampaignDetail />} />
        <Route path="/brand/:brandId" element={<BrandLayout />}>
          <Route index element={<Navigate to="docs" replace />} />
          <Route path="settings" element={<BrandSettingsLayout />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<BrandProfilePage />} />
            <Route path="strategy" element={<BrandLegacyRedirect target="artifacts/strategy" />} />
            <Route path="voice" element={<BrandVoiceSettings />} />
            <Route path="context-docs" element={<BrandContextDocs />} />
            <Route path="connections" element={<BrandConnectionsPage />} />
            <Route path="surfaces" element={<BrandSurfacesPage />} />
            <Route path="surfaces/:surfaceId/content" element={<PropertyContentCatalogPage />} />
            <Route path="surfaces/:surfaceId/content/:itemId" element={<PropertyContentDetailPage />} />
            <Route path="corrections" element={<BrandCorrectionsPage />} />
            <Route path="visuals" element={<BrandVisualsPage />} />
          </Route>
          <Route path="profile" element={<BrandLegacyRedirect target="settings/profile" />} />
          <Route path="strategy" element={<BrandLegacyRedirect target="artifacts/strategy" />} />
          <Route path="voice" element={<BrandLegacyRedirect target="artifacts/voice" />} />
          <Route path="voice_ai" element={<BrandLegacyRedirect target="docs" />} />
          <Route path="connections" element={<BrandLegacyRedirect target="settings/connections" />} />
          <Route path="corrections" element={<BrandLegacyRedirect target="settings/corrections" />} />
          <Route path="skills" element={<BrandLegacyRedirect target="docs" />} />
          <Route path="monitor" element={<BrandLegacyRedirect target="docs" />} />
          <Route path="voice-discovery" element={<VoiceDiscovery />} />
          <Route path="research" element={<BrandResearch />} />
          <Route path="research/:runId" element={<ResearchRunDetail />} />
          <Route path="research/packages/:packageId" element={<BrandResearchPackage />} />
          <Route path="corpus" element={<BrandLegacyRedirect target="corpus" />} />
        </Route>
        <Route path="/onboarding" element={<Onboarding />} />
        {/*
          Persistent workspace shell (PRD-0085): the WorkspaceTabsProvider +
          WorkspaceTabBar live here, ABOVE the work-object routes, so opening a
          feed item (which navigates to a sibling work-object route) does NOT
          unmount the provider. The tab persists and the destination page renders
          underneath the still-visible tab bar. These routes stay directly
          loadable by URL (deep-linking) — the shell just adds the bar.
        */}
        <Route element={<WorkspaceShell />}>
          <Route path="/home" element={<WorkspacePage />} />
          <Route path="/agent/:brandId" element={<AgentUIPage />} />
          <Route path="/brand/:brandId/docs" element={<BrandDocsPage />} />
          <Route path="/brand/:brandId/assets" element={<AssetLibrary />} />
          <Route path="/brand/:brandId/assets/imports" element={<ShopifyImportListPage />} />
          <Route path="/brand/:brandId/assets/imports/:jobId" element={<ShopifyImportDetailPage />} />
          <Route element={<PublishingStudioLayout />}>
            <Route path="/brand/:brandId/publishing" element={<PublishingWorkspacePage />} />
            <Route path="/brand/:brandId/publishing/new" element={<YouTubeComposerPage />} />
            <Route path="/brand/:brandId/publishing/variants/:variantId/edit" element={<YouTubeComposerPage />} />
            <Route path="/brand/:brandId/publishing/variants/:variantId/review" element={<PublishingReviewPage />} />
            <Route path="/brand/:brandId/publishing/deliveries/:deliveryId" element={<PublishingDeliveryPage />} />
            <Route path="/brand/:brandId/youtube/videos" element={<YouTubeLibraryPage />} />
            <Route path="/brand/:brandId/youtube/videos/:videoId" element={<YouTubeVideoDetailPage />} />
            <Route path="/brand/:brandId/youtube/videos/:videoId/analytics" element={<YouTubeAnalyticsPage />} />
            <Route path="/brand/:brandId/youtube/analytics" element={<YouTubeAnalyticsPage />} />
            <Route path="/brand/:brandId/youtube/readiness" element={<YouTubeReadinessPage />} />
          </Route>
          <Route path="/brand/:brandId/review" element={<BrandReview />} />
          <Route path="/brand/:brandId/analytics" element={<AnalyticsIndexPage />} />
          <Route path="/brand/:brandId/analytics/settings/:assignmentId" element={<AnalyticsSourceSettingsPage />} />
          <Route path="/brand/:brandId/data-views" element={<DataViewsPage />} />
          <Route path="/brand/:brandId/data-views/variants/:variantId" element={<DataViewVariantReviewPage />} />
          <Route path="/brand/:brandId/analytics/property/:surfaceId" element={<AnalyticsPropertyPage />} />
          <Route path="/brand/:brandId/analytics/property/:surfaceId/source/native/:assignmentId" element={<AnalyticsSourcePage />} />
          <Route path="/brand/:brandId/analytics/property/:surfaceId/source/native/:assignmentId/keyword/:query" element={<AnalyticsKeywordPage />} />
          <Route path="/brand/:brandId/analytics/property/:surfaceId/source/referrer/:assignmentId/:platform" element={<AnalyticsReferrerSourcePage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/start/:slug" element={<StartJobPage />} />
          <Route path="/workflows" element={<WorkflowsPage />} />
          <Route path="/review/:runId" element={<Review />} />
          <Route path="/runs/:runId" element={<RunDetail />} />
          <Route path="/runs/:runId/trace" element={<RunTracePage />} />
          <Route path="/gates/:gateId" element={<GateReview />} />
          {/* Durable output details are active work; their library index remains
              standalone below under §7.1.3. */}
          <Route path="/outputs/:family/:key" element={<OutputDetail />} />
          <Route path="/brand/:brandId/artifacts/:artifactType" element={<BrandArtifactWorkspace />} />
        </Route>
        <Route path="/corpus" element={<Corpus />} />
        <Route path="/ideas/:runId" element={<Ideas />} />
        <Route path="/chat/:threadId" element={<Chat />} />
        <Route path="/campaigns" element={<CampaignsRouteEntry />} />
        <Route path="/review" element={<ReviewRouteEntry />} />
        <Route path="/performance/:runId" element={<Performance />} />
        <Route path="/brand" element={<Brand />} />
        <Route path="/billing" element={<BillingRedirect />} />
        <Route path="/usage" element={<Usage />} />
        <Route path="/playbooks" element={<Playbooks />} />
        <Route path="/skills/:threadId" element={<Skills />} />
        <Route path="/runs" element={<Runs />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/outputs" element={<Outputs />} />
        <Route path="/settings/youtube-policy" element={<YouTubePolicySettingsPage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/connections" element={<ConnectionsSettingsPage />} />
        <Route path="/settings/connections/:connectionId" element={<ConnectionDetailPage />} />
        <Route path="/settings/tokens" element={<ApiTokensPage />} />
        <Route path="/brands" element={<Brands />} />
        <Route path="/brands/new" element={<BrandCreate />} />
        <Route path="/diagnostics" element={<Diagnostics />} />
        {/* Catch-all: a bad link renders "Page not found", never a blank page. */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
